import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Calendar, Clock, Plus, Trash2, Edit2, Save, X, 
  Loader2, MapPin, ChevronDown, ChevronUp
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ACTIVITY_TYPES = [
  'Team Training',
  '1-2-1 Training',
  'Match',
  'Friendly Match',
  'Academy Session',
  'Fitness Training',
  'Recovery Session',
  'Skills Session',
  'Goalkeeper Training',
  'Other'
];

export default function WeeklyTrainingPlan({ childId, childName, canEdit = true }) {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expanded, setExpanded] = useState(true);
  
  // Form state for new session
  const [newSession, setNewSession] = useState({
    day: 'Monday',
    activity: 'Team Training',
    start_time: '18:00',
    end_time: '19:00',
    location: '',
    notes: ''
  });
  
  // Editing sessions
  const [editingSessions, setEditingSessions] = useState([]);

  const loadCurrentPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/training-plan/weekly/${childId}/current`);
      if (response.data.plan) {
        setCurrentPlan(response.data.plan);
        setEditingSessions(response.data.plan.sessions || []);
      }
    } catch (error) {
      console.error('Failed to load training plan:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadCurrentPlan();
  }, [loadCurrentPlan]);

  const calculateDuration = (start, end) => {
    try {
      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);
      return (endH * 60 + endM) - (startH * 60 + startM);
    } catch {
      return 60;
    }
  };

  const formatTime = (time24) => {
    try {
      const [hours, minutes] = time24.split(':');
      const h = parseInt(hours);
      const ampm = h >= 12 ? 'pm' : 'am';
      const h12 = h % 12 || 12;
      return `${h12}:${minutes}${ampm}`;
    } catch {
      return time24;
    }
  };

  const handleAddSession = () => {
    const duration = calculateDuration(newSession.start_time, newSession.end_time);
    const sessionWithDuration = {
      ...newSession,
      duration_minutes: duration
    };
    
    setEditingSessions([...editingSessions, sessionWithDuration]);
    setShowAddForm(false);
    setNewSession({
      day: 'Monday',
      activity: 'Team Training',
      start_time: '18:00',
      end_time: '19:00',
      location: '',
      notes: ''
    });
    
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleRemoveSession = (index) => {
    setEditingSessions(editingSessions.filter((_, i) => i !== index));
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleSavePlan = async () => {
    if (editingSessions.length === 0) {
      toast.error('Please add at least one training session');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/training-plan/weekly/${childId}`, {
        sessions: editingSessions,
        notes: ''
      });
      
      toast.success('Training plan saved!');
      setIsEditing(false);
      loadCurrentPlan();
    } catch (error) {
      toast.error('Failed to save training plan');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (currentPlan) {
      setEditingSessions(currentPlan.sessions || []);
    } else {
      setEditingSessions([]);
    }
    setIsEditing(false);
    setShowAddForm(false);
  };

  // Group sessions by day
  const sessionsByDay = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day] = editingSessions.filter(s => s.day === day);
    return acc;
  }, {});

  const totalWeeklyMinutes = editingSessions.reduce((sum, s) => sum + (s.duration_minutes || 60), 0);
  const totalWeeklyHours = (totalWeeklyMinutes / 60).toFixed(1);

  if (loading) {
    return (
      <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
          <span className="ml-2 text-zinc-400">Loading training plan...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="weekly-training-plan">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div 
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <Calendar className="h-6 w-6 text-[#02b5ff]" />
          <div>
            <h2 className="text-lg font-bold uppercase tracking-widest text-white">Weekly Training Plan</h2>
            <p className="text-xs text-zinc-500 mt-1">
              {editingSessions.length} sessions • {totalWeeklyHours} hours/week
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-zinc-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-zinc-500" />
          )}
        </div>
        
        {canEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  onClick={() => setShowAddForm(true)}
                  variant="outline"
                  className="h-9 px-4 border-[#02b5ff]/50 text-[#02b5ff] hover:bg-[#02b5ff]/10 text-xs"
                  data-testid="add-another-session-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add More
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  className="h-9 px-4 border-zinc-700 text-zinc-400 hover:text-white text-xs"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePlan}
                  disabled={saving}
                  className="h-9 px-4 bg-green-600 hover:bg-green-500 text-white text-xs"
                  data-testid="save-plan-button"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save Plan
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setShowAddForm(true)}
                className="h-9 px-4 bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white text-xs"
                data-testid="add-session-button"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Session
              </Button>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <>
          {/* Add Session Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-700 rounded-lg" data-testid="add-session-form">
              <h3 className="text-sm font-bold text-zinc-400 uppercase mb-4">Add Training Session</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Day</label>
                  <select
                    value={newSession.day}
                    onChange={(e) => setNewSession({ ...newSession, day: e.target.value })}
                    className="w-full p-2 bg-zinc-800 border border-zinc-700 text-white text-sm rounded"
                    data-testid="session-day-select"
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Activity</label>
                  <select
                    value={newSession.activity}
                    onChange={(e) => setNewSession({ ...newSession, activity: e.target.value })}
                    className="w-full p-2 bg-zinc-800 border border-zinc-700 text-white text-sm rounded"
                    data-testid="session-activity-select"
                  >
                    {ACTIVITY_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Start Time</label>
                  <Input
                    type="time"
                    value={newSession.start_time}
                    onChange={(e) => setNewSession({ ...newSession, start_time: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    data-testid="session-start-time"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">End Time</label>
                  <Input
                    type="time"
                    value={newSession.end_time}
                    onChange={(e) => setNewSession({ ...newSession, end_time: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    data-testid="session-end-time"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Location</label>
                  <Input
                    type="text"
                    placeholder="Optional"
                    value={newSession.location}
                    onChange={(e) => setNewSession({ ...newSession, location: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                  />
                </div>
                
                <div className="flex items-end gap-2">
                  <Button
                    onClick={handleAddSession}
                    className="h-9 px-4 bg-green-600 hover:bg-green-500 text-white text-xs flex-1"
                    data-testid="confirm-add-session"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                  <Button
                    onClick={() => setShowAddForm(false)}
                    variant="outline"
                    className="h-9 px-3 border-zinc-700 text-zinc-400"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Schedule Grid */}
          {editingSessions.length === 0 ? (
            <div className="text-center py-12 border-t border-zinc-800" data-testid="no-training-sessions">
              <Calendar className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm mb-2">No training sessions scheduled</p>
              <p className="text-zinc-600 text-xs">Add your weekly training schedule above</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="training-schedule">
              {DAYS_OF_WEEK.map(day => {
                const daySessions = sessionsByDay[day];
                if (daySessions.length === 0) return null;
                
                return (
                  <div key={day} className="bg-zinc-900/30 border border-zinc-800 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/30">
                      <span className="text-sm font-bold text-white uppercase">{day}</span>
                      <span className="text-xs text-zinc-500">
                        {daySessions.length} session{daySessions.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="divide-y divide-zinc-800/50">
                      {daySessions.map((session, idx) => {
                        const sessionIndex = editingSessions.findIndex(s => s === session);
                        return (
                          <div 
                            key={idx} 
                            className="flex items-center justify-between px-4 py-3 hover:bg-zinc-800/20"
                            data-testid={`session-${day}-${idx}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 text-[#02b5ff]">
                                <Clock className="h-4 w-4" />
                                <span className="text-sm font-mono">
                                  {formatTime(session.start_time)} - {formatTime(session.end_time)}
                                </span>
                              </div>
                              <span className="text-white font-medium">{session.activity}</span>
                              {session.location && (
                                <span className="flex items-center gap-1 text-xs text-zinc-500">
                                  <MapPin className="h-3 w-3" />
                                  {session.location}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-zinc-500 font-mono">
                                {session.duration_minutes || 60} min
                              </span>
                              {canEdit && (
                                <button
                                  onClick={() => handleRemoveSession(sessionIndex)}
                                  className="p-1.5 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400"
                                  title="Remove session"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Weekly Summary */}
          {editingSessions.length > 0 && (
            <div className="mt-4 p-4 bg-[#02b5ff]/10 border border-[#02b5ff]/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#02b5ff]">Weekly Training Summary</span>
                <div className="flex gap-6">
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{editingSessions.length}</div>
                    <div className="text-xs text-zinc-500">Sessions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{totalWeeklyHours}</div>
                    <div className="text-xs text-zinc-500">Hours</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
